import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import ProductSelection from './components/ProductSelection';
import LeadSelection from './components/LeadSelection';
import DrawExecution from './components/DrawExecution';
import { Product } from '../../../types';
import { usePageTitle } from '../../../hooks/usePageTitle';

type Step = 1 | 2 | 3;

interface Lead {
  id: string;
  customer_name: string;
  whatsapp: string;
  customer_birthdate: string | null;
  last_wheel_name: string;
  created_at: string;
}

const ManualDrawPage: React.FC = () => {
  usePageTitle('Sorteio Manual');
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [drawResult, setDrawResult] = useState<any>(null);

  const handleProductsSelected = (products: Product[]) => {
    setSelectedProducts(products);
    setCurrentStep(2);
  };

  const handleLeadsSelected = (leads: Lead[]) => {
    setSelectedLeads(leads);
    setCurrentStep(3);
  };

  const handleDrawCompleted = (result: any) => {
    setDrawResult(result);
  };

  const handleResetDraw = () => {
    setCurrentStep(1);
    setSelectedProducts([]);
    setSelectedLeads([]);
    setDrawResult(null);
  };

  const steps: Array<{ num: 1 | 2 | 3; label: string }> = [
    { num: 1, label: 'Produtos' },
    { num: 2, label: 'Participantes' },
    { num: 3, label: 'Sortear' },
  ];

  const isStepDone = (num: 1 | 2 | 3) => {
    if (num === 3) return !!drawResult;
    return currentStep > num;
  };
  const isStepActive = (num: 1 | 2 | 3) => currentStep >= num;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-4 sm:mb-6 overflow-x-auto pb-2">
      <div className="flex items-center space-x-2 sm:space-x-4 min-w-max">
        {steps.map((step, idx) => {
          const active = isStepActive(step.num);
          const done = isStepDone(step.num);
          return (
            <React.Fragment key={step.num}>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full text-sm sm:text-base font-bold transition-colors ${
                    active
                      ? 'bg-bordo text-creme'
                      : 'bg-tinta/10 text-tinta-soft border border-tinta/15'
                  }`}
                >
                  {done ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : step.num}
                </div>
                <span
                  className={`ml-1.5 sm:ml-2 text-xs sm:text-sm font-bold whitespace-nowrap ${
                    active ? 'text-bordo' : 'text-tinta-soft'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`w-2 sm:w-12 h-0.5 flex-shrink-0 transition-colors ${
                    isStepActive((step.num + 1) as 1 | 2 | 3) ? 'bg-bordo' : 'bg-tinta/15'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-black text-tinta leading-tight">
          Sorteio <span className="italic text-bordo">manual</span>
        </h1>
        <p className="mt-2 text-sm text-tinta-soft">
          Selecione os produtos e participantes para realizar um sorteio manual
        </p>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-4 sm:p-6">
        {currentStep === 1 && (
          <ProductSelection
            selectedProducts={selectedProducts}
            onProductsSelected={handleProductsSelected}
          />
        )}

        {currentStep === 2 && (
          <LeadSelection
            selectedLeads={selectedLeads}
            onLeadsSelected={handleLeadsSelected}
            onBack={() => setCurrentStep(1)}
          />
        )}

        {currentStep === 3 && (
          <DrawExecution
            selectedProducts={selectedProducts}
            selectedLeads={selectedLeads}
            onDrawCompleted={handleDrawCompleted}
            onBack={() => setCurrentStep(2)}
            onReset={handleResetDraw}
            drawResult={drawResult}
          />
        )}
      </div>
    </div>
  );
};

export default ManualDrawPage;
